import { type ReactNode, useEffect } from 'react'
import { useAppDispatch, useAuth } from '@/app/hooks'
import { useLazyGetMeQuery, useLazyGetProfileQuery, useRefreshMutation } from '@/features/auth/auth.api'
import { setInitializing, setUser, clearUser } from '@/features/auth/auth.slice'
import { syncCsrfToken } from '@/services/axios.instance'

interface AppInitializerProps {
  children: ReactNode
}

/**
 * Runs GET /auth/me once on mount.
 * - On success: fills Redux with the user from the active session.
 * - On failure: clears Redux (session expired or not logged in).
 *
 * Automatically refreshes the session token every 15 minutes when authenticated.
 */
export default function AppInitializer({ children }: AppInitializerProps) {
  const dispatch = useAppDispatch()
  const { isAuthenticated } = useAuth()
  const [getMe] = useLazyGetMeQuery()
  const [getProfile] = useLazyGetProfileQuery()
  const [refresh] = useRefreshMutation()

  useEffect(() => {
    dispatch(setInitializing(true))

    // 1. Sync CSRF token on mount (per BEST_PRACTICES.md Flow 1)
    syncCsrfToken().catch((err) => {
      console.warn('[AUTH] CSRF token initialization warning:', err)
    })

    // 2. Validate active session
    getMe()
      .unwrap()
      .then(async (res) => {
        let userData = res.data
        try {
          const profileRes = await getProfile().unwrap()
          if (profileRes?.data) {
            userData = {
              ...userData,
              firstName: profileRes.data.firstName,
              lastName: profileRes.data.lastName,
              fullName: `${profileRes.data.firstName || ''} ${profileRes.data.lastName || ''}`.trim(),
              phoneNumber: profileRes.data.phoneNumber,
            }
          }
        } catch {
          // Ignore profile error if endpoint fails
        }
        dispatch(setUser(userData))
      })
      .catch(() => {
        dispatch(clearUser())
      })

    // Listen for session expiry event from Axios interceptor (BEST_PRACTICES.md Section 6)
    const handleExpired = () => {
      dispatch(clearUser())
      const path = window.location.pathname
      const isPublicRoute =
        path === '/login' ||
        path === '/login/' ||
        path === '/register' ||
        path === '/register/' ||
        path.startsWith('/register') ||
        path === '/forgot-password' ||
        path === '/forgot-password/' ||
        path.startsWith('/forgot-password') ||
        path === '/unauthorized' ||
        path.startsWith('/verify-certificate') ||
        path.startsWith('/certificates/verify')

      if (!isPublicRoute) {
        window.location.replace('/login')
      }
    }
    window.addEventListener('auth:session-expired', handleExpired)
    return () => window.removeEventListener('auth:session-expired', handleExpired)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Silent refresh every 15 minutes when authenticated
  useEffect(() => {
    if (!isAuthenticated) return
// const ONE_MINUTE = 1 * 60 * 1000;
    const FIFTEEN_MINUTES = 15 * 60 * 1000
    const refreshInterval = setInterval(() => {
      refresh()
    }, FIFTEEN_MINUTES)

    return () => clearInterval(refreshInterval)
  }, [isAuthenticated, refresh])

  return <>{children}</>
}
